import { IntlShape, FormatDateOptions, createIntlCache, createIntl } from "react-intl";
import { LocaleEnums } from "models/enums";
import Translations from "translations";
import { UtilsHelper } from "utils/helpers";
import { NumberFormatOptionsCurrencyDisplay } from "models/utilTypes/currencyType";
import { NumberFormatOptionsSignDisplay } from "@formatjs/ecma402-abstract";

class LocalizationHelper {
  intl: IntlShape;

  constructor() {
    this.intl = this.getIntl(LocaleEnums.LocaleType.sv);
  }

  getIntl = (lang: LocaleEnums.LocaleType) => {
    const cache = createIntlCache();

    const intl = createIntl(
      {
        locale: lang,
        defaultLocale: "sv",
        messages: Translations[lang],
      },
      cache
    );
    this.intl = intl;

    return intl;
  };

  getFormattedMessage = (
    id: string,
    defaultMessage?: string,
    values?: Record<string, string | number | boolean | Date | undefined>
  ) => {
    return id
      ? id.match(/\w+\.\w+/) || (Translations.sv as any)[id] !== undefined
        ? this.intl.formatMessage(
            {
              id: id,
              defaultMessage: defaultMessage ?? id,
            },
            values
          )
        : id
      : "";
  };

  getMessageForAllLocales = (id: string): string[] =>
    Object.values(LocaleEnums.LocaleType)
      .map((locale) => (Translations[locale] as Record<string, string>)?.[id])
      .filter((message): message is string => !!message);

  getFormattedCurrency = (
    value: number,
    currency?: string,
    minFractionDigits: number = 0,
    maxFractionDigits: number = 2,
    currencyDisplay?: NumberFormatOptionsCurrencyDisplay,
    signDisplay?: NumberFormatOptionsSignDisplay
  ) => {
    return currency !== undefined
      ? this.intl.formatNumber(value, {
          style: "currency",
          currency: currency,
          minimumFractionDigits: minFractionDigits,
          maximumFractionDigits: maxFractionDigits,
          currencyDisplay: currencyDisplay,
          signDisplay: signDisplay ?? "auto",
        })
      : this.intl.formatNumber(value, {
          minimumFractionDigits: minFractionDigits,
          maximumFractionDigits: maxFractionDigits,
          signDisplay: signDisplay ?? "auto",
        });
  };

  getFormattedNumber = (
    value: number,
    minFractionDigits: number = 0,
    maxFractionDigits: number = 0,
    signDisplay: NumberFormatOptionsSignDisplay = "auto"
  ) => {
    return this.intl.formatNumber(value, {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      signDisplay: signDisplay,
    });
  };

  getFormattedPercentage = (
    value: number,
    minFractionDigits: number = 0,
    maxFractionDigits: number = 0,
    signDisplay: NumberFormatOptionsSignDisplay = "auto"
  ) => {
    return this.intl.formatNumber(value, {
      style: "percent",
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      signDisplay: signDisplay,
    });
  };

  getCurrencySymbol = (currency: string) => {
    return this.getFormattedCurrency(0, currency).replace("0", "").trim();
  };

  getNumberSeparators = () => {
    const numberFormat = this.intl.formatters.getNumberFormat(this.intl.locale);
    if (!numberFormat || !numberFormat.formatToParts || typeof numberFormat.formatToParts !== "function") {
      return {};
    }
    const parts = numberFormat.formatToParts(9999.99);

    return {
      decimalSeparator: parts.find((i) => i.type === "decimal")?.value,
      thousandSeparator: parts.find((i) => i.type === "group")?.value,
    };
  };

  getFormattedDate = (date: string | number | Date, opts?: FormatDateOptions, keepOriginalCase?: boolean) => {
    let formattedDate = this.intl.formatDate(date, opts);

    if (!keepOriginalCase) formattedDate = UtilsHelper.capitalizeFirst(formattedDate);
    if (formattedDate.endsWith(".")) return formattedDate.substring(0, formattedDate.length - 1);
    return formattedDate;
  };

  getFormattedShortMonth = (date: Date) => {
    return this.getFormattedDate(date, { month: "short" });
  };

  getTranslatedEnumValue = (translationKeyPrefix: string, enumKey: number | string | undefined) => {
    if (typeof enumKey === "undefined") {
      return "";
    }
    return this.getFormattedMessage(`${translationKeyPrefix}_${enumKey}`);
  };
}

const LocalizationHelperInstance = new LocalizationHelper();
export default LocalizationHelperInstance;
